import { zodResolver } from '@hookform/resolvers/zod';
import { map } from 'ramda';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SubheadingText from '@ui/atoms/SubheadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CheckboxButton from '@ui/forms/CheckboxButton';

import BackAndNextNav from '@ui/molecules/BackAndNextNav';
import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import DetailText from '@ui/molecules/DetailText';
import { IDetailTextProps } from '@ui/molecules/DetailText/DetailText.component';
import LargeDetailSelection from '@ui/molecules/LargeDetailSelection';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { commonLabels } from '@utils/button';
import { exists } from '@utils/general';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IDetail } from '@folx-types/text';

import theme from '@theme/FolxTheme';

import { schema } from './MemberEstrogenOtherMedicationStep.data';

export interface IMemberEstrogenOtherMedicationStepPageContent {
	pageHeader: string;
	pageInfo: string;
	medicationHeader: string;
	spironolactone: IDetail;
	finasteride: IDetail;
	dutasteride: IDetail;
	progesterone: IDetail;
	unsure: IDetail;
}

interface IMemberEstrogenOtherMedicationStepProps extends IFlowStepComponentProps {
	pageContent: IMemberEstrogenOtherMedicationStepPageContent;
}

const SpacedGridItem = spaced(CenteredGridItem);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const FullWidthForm = fullWidth('form');
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);
const FullWidthDetail = fullWidth<IDetailTextProps>(DetailText);

const MemberEstrogenOtherMedicationStep: FC<IMemberEstrogenOtherMedicationStepProps> = ({
	pageContent,
	existingFormData,
	handleFormSubmission = () => {},
	handleNavBack = () => {},
	stepKey,
}) => {
	const { pageHeader, pageInfo, medicationHeader } = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		control,
		watch,
		register,
		setValue,
		formState: { errors },
	} = form;

	const onSubmit = (data: Record<string, unknown>) => {
		handleFormSubmission(data, stepKey);
	};

	const medications = ['spironolactone', 'finasteride', 'dutasteride', 'progesterone', 'unsure'];

	const getMedications = map((medication) => {
		const medDetail: IDetail = exists(pageContent?.[medication])
			? pageContent[medication]
			: { title: '', description: '' };

		return (
			<CheckboxButton {...register(medication)} optionLabel={medDetail.title}>
				<FullWidthDetail title={medDetail.title} description={medDetail.description} />
			</CheckboxButton>
		);
	}, medications);

	return (
		<SpacedGridItem marginBottom="xxxl">
			<SpacedColumn marginBottom="lg" gap="sm">
				<HeadingText>{pageHeader}</HeadingText>
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageInfo }} />
			</SpacedColumn>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
				<SpacedColumn marginBottom="xxl" gap="sm">
					<SubheadingText>{medicationHeader}</SubheadingText>
					{getMedications}
				</SpacedColumn>
				<BackAndNextNav
					backOnClick={() => handleNavBack(stepKey)}
					submitLabel={commonLabels.next}
				/>
			</FullWidthForm>
		</SpacedGridItem>
	);
};

export default MemberEstrogenOtherMedicationStep;
